export type BubbleRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type BubbleLayoutSize = {
  width: number;
  height: number;
};

export type BubbleLayoutBox = {
  index: number;
  width: number;
  height: number;
  baseX: number;
  baseY: number;
};

export type BubblePlacement = {
  index: number;
  x: number;
  y: number;
  cost: number;
  rect: BubbleRect;
};

export type BubbleLayoutOptions = {
  tailHeight: number;
  margin: number;
  inset: number;
  candidateStep: number;
  candidateRadius: number;
  candidateLimit: number;
  searchNodeLimit: number;
};

type BubbleCandidate = {
  x: number;
  y: number;
  cost: number;
  rect: BubbleRect;
};

type BubbleLayoutWorkBox = BubbleLayoutBox & {
  area: number;
  candidates: Array<BubbleCandidate>;
};

// 꼬리는 .place-item-box의 두 의사요소로 구성된다:
//   ::before { bottom: -14px } — 바깥(테두리색) 삼각형
//   ::after  { bottom: -12px } — 안쪽(흰색) 삼각형
// 박스 아래로 가장 많이 튀어나오는 것은 ::before 14px이므로 tailHeight = 14.
// SCSS에서 두 값 중 하나라도 더 바깥으로 바뀌면 이 값도 함께 맞춰야 한다.
const DEFAULT_BUBBLE_LAYOUT_OPTIONS: BubbleLayoutOptions = {
  tailHeight: 14,
  margin: 8,
  inset: 2,
  candidateStep: 12,
  candidateRadius: 14,
  candidateLimit: 144,
  searchNodeLimit: 200000,
};

// 2단계 전략:
//   1) findNonOverlappingPlacement — 겹침 없는 배치를 깊이 우선 분기 한정으로 탐색.
//      비용(=base 위치에서의 거리) 합이 최소인 해를 선호한다.
//   2) 1)이 실패(겹침 없는 배치 불가능)하면 findFallbackPlacement —
//      박스별로 겹침 페널티가 가장 작은 후보를 그리디하게 선택.
// area 내림차순 정렬은 큰 박스(=배치 자유도가 낮음)부터 시도해
// 분기 한정 탐색의 가지치기 효율을 높이는 휴리스틱이다.
export function resolveBubblePlacements(
  boxes: Array<BubbleLayoutBox>,
  viewSize: BubbleLayoutSize,
  optionOverrides: Partial<BubbleLayoutOptions> = {},
): Array<BubblePlacement> {
  const options = {
    ...DEFAULT_BUBBLE_LAYOUT_OPTIONS,
    ...optionOverrides,
  };
  const orderedBoxes = boxes
    .map(box => ({
      ...box,
      area: box.width * box.height,
      candidates: createBubbleCandidates(box, viewSize, options),
    }))
    .sort((a, b) => b.area - a.area || a.index - b.index);

  const exact = findNonOverlappingPlacement(orderedBoxes, options);
  if (exact) {
    return exact;
  }

  return findFallbackPlacement(orderedBoxes, options);
}

function createBubbleCandidates(
  box: BubbleLayoutBox,
  viewSize: BubbleLayoutSize,
  options: BubbleLayoutOptions,
): Array<BubbleCandidate> {
  const candidates = new Array<BubbleCandidate>();
  const seen = new Set<string>();

  for (let radius = 0; radius <= options.candidateRadius; ++radius) {
    for (let dx = -radius; dx <= radius; ++dx) {
      for (let dy = -radius; dy <= radius; ++dy) {
        if (radius !== 0 && Math.max(Math.abs(dx), Math.abs(dy)) !== radius) {
          continue;
        }

        const x = Math.round(box.baseX + dx * options.candidateStep);
        const y = Math.round(box.baseY + dy * options.candidateStep);
        const key = `${x}:${y}`;
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);

        if (!isInsideViewBox(x, y, box.width, box.height, viewSize, options)) {
          continue;
        }

        const rect = toBubbleRect(x, y, box.width, box.height, options);
        const distanceCost = Math.abs(x - box.baseX) + Math.abs(y - box.baseY);
        candidates.push({x, y, cost: distanceCost, rect});
      }
    }
  }

  candidates.sort((a, b) => a.cost - b.cost);
  return candidates.slice(0, options.candidateLimit);
}

function isInsideViewBox(
  x: number,
  y: number,
  width: number,
  height: number,
  viewSize: BubbleLayoutSize,
  options: BubbleLayoutOptions,
): boolean {
  return x >= options.inset
    && y >= options.inset
    && x + width <= viewSize.width - options.inset
    && y + height + options.tailHeight <= viewSize.height - options.inset;
}

function toBubbleRect(
  x: number,
  y: number,
  width: number,
  height: number,
  options: BubbleLayoutOptions,
): BubbleRect {
  return {
    left: x,
    top: y,
    right: x + width,
    bottom: y + height + options.tailHeight,
  };
}

function findNonOverlappingPlacement(
  boxes: Array<BubbleLayoutWorkBox>,
  options: BubbleLayoutOptions,
): Array<BubblePlacement> | null {
  let bestCost = Number.POSITIVE_INFINITY;
  let bestPlacements: Array<BubblePlacement> | null = null;
  let visitedNodes = 0;
  let exhausted = false;

  const search = (
    boxIndex: number,
    placed: Array<BubblePlacement>,
    cost: number,
  ): void => {
    if (++visitedNodes > options.searchNodeLimit) {
      exhausted = true;
      return;
    }
    if (cost >= bestCost) {
      return;
    }

    if (boxIndex >= boxes.length) {
      bestCost = cost;
      bestPlacements = [...placed];
      return;
    }

    const box = boxes[boxIndex];
    for (const candidate of box.candidates) {
      if (placed.some(placement => hasOverlap(candidate.rect, placement.rect, options.margin))) {
        continue;
      }

      placed.push({...candidate, index: box.index});
      search(boxIndex + 1, placed, cost + candidate.cost);
      placed.pop();
    }
  };

  search(0, [], 0);

  if (exhausted) {
    if (bestPlacements) {
      // 겹침 없는 배치는 찾았으나 탐색이 도중에 잘렸으니 전역 최적은 아닐 수 있다.
      console.warn(
        `dialect-map-bubble-layout: 탐색 노드 한도(${options.searchNodeLimit}) 도달 — 현재까지 찾은 겹침 없는 배치 반환(전역 최적이 아닐 수 있음)`,
        {boxCount: boxes.length},
      );
    }
    else {
      // 한도 안에서 겹침 없는 배치를 찾지 못해 곧 fallback으로 진입.
      console.warn(
        `dialect-map-bubble-layout: 탐색 노드 한도(${options.searchNodeLimit}) 도달 — 겹침 없는 배치 미발견, fallback으로 진입`,
        {boxCount: boxes.length},
      );
    }
  }

  return bestPlacements;
}

function findFallbackPlacement(
  boxes: Array<BubbleLayoutWorkBox>,
  options: BubbleLayoutOptions,
): Array<BubblePlacement> {
  const placements = new Array<BubblePlacement>();

  boxes.forEach(box => {
    let bestCandidate: BubbleCandidate | undefined;
    let bestCost = Number.POSITIVE_INFINITY;

    box.candidates.forEach(candidate => {
      const overlapCost = placements.reduce(
        (sum, placement) => sum + overlapArea(candidate.rect, placement.rect, options.margin),
        0,
      );
      // 겹침 면적에 1000을 곱해 base 거리 비용보다 절대적으로 무겁게 만든다.
      // 결과적으로 "조금 더 멀더라도 겹치지 않는 위치"를 우선 선택한다.
      const cost = candidate.cost + overlapCost * 1000;
      if (cost < bestCost) {
        bestCandidate = candidate;
        bestCost = cost;
      }
    });

    if (bestCandidate) {
      placements.push({...bestCandidate, index: box.index});
    }
  });

  return placements;
}

function hasOverlap(a: BubbleRect, b: BubbleRect, margin: number): boolean {
  return overlapArea(a, b, margin) > 0;
}

function overlapArea(a: BubbleRect, b: BubbleRect, margin: number): number {
  const overlapWidth = Math.min(a.right + margin, b.right) - Math.max(a.left - margin, b.left);
  const overlapHeight = Math.min(a.bottom + margin, b.bottom) - Math.max(a.top - margin, b.top);

  return Math.max(0, overlapWidth) * Math.max(0, overlapHeight);
}
